import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';
import { Toaster as Sonner, toast } from '@/lib/toast';
import { useIsMobile } from '@/hooks/use-mobile';

type ToasterProps = React.ComponentProps<typeof Sonner>;

/**
 * Desktop toast placement philosophy:
 *  - Vertical: fixed `top: 16px`. Sits just inside the top-right corner,
 *    below the header bar. No avatar-relative math.
 *  - Horizontal: keep toasts clear of the user menu. When the profile dropdown
 *    is open, measure its left edge; otherwise measure the avatar button's left
 *    edge. In both cases a 16px gap is applied. A static fallback (84px) covers
 *    the brief window before the anchor mounts so the toast never lands on top
 *    of the menu.
 * Mobile is untouched (top-center + safe-area).
 */
const GAP = 16;
const FALLBACK_RIGHT = 84; // header px-6 (24) + right-section mr-1 (4) + avatar (40) + gap (16)

function useDesktopToastRight(enabled: boolean) {
	const [right, setRight] = useState<string>(`${FALLBACK_RIGHT}px`);

	useEffect(() => {
		if (!enabled) return;

		let ro: ResizeObserver | null = null;

		const measure = () => {
			const dropdown = document.querySelector<HTMLElement>('[data-user-menu-dropdown]');
			if (dropdown && window.getComputedStyle(dropdown).display !== 'none') {
				const left = dropdown.getBoundingClientRect().left;
				setRight(`${Math.max(FALLBACK_RIGHT, window.innerWidth - left + GAP)}px`);
				return;
			}
			const anchor = document.querySelector<HTMLElement>('[data-profile-anchor]');
			if (anchor) {
				const left = anchor.getBoundingClientRect().left;
				setRight(`${Math.max(FALLBACK_RIGHT, window.innerWidth - left + GAP)}px`);
				return;
			}
			setRight(`${FALLBACK_RIGHT}px`);
		};

		const startObserving = () => {
			ro?.disconnect();
			ro = new ResizeObserver(measure);
			const dropdown = document.querySelector('[data-user-menu-dropdown]');
			const anchor = document.querySelector('[data-profile-anchor]');
			if (dropdown) ro.observe(dropdown);
			if (anchor) ro.observe(anchor);
			measure();
		};

		startObserving();

		// Watch for the portal-mounted dropdown appearing/disappearing.
		const mo = new MutationObserver(startObserving);
		mo.observe(document.body, { childList: true, subtree: true });

		window.addEventListener('resize', measure);

		// Anchor/dropdown may mount after Toaster (auth flow). Poll briefly.
		const poll = window.setInterval(() => {
			if (document.querySelector('[data-profile-anchor]')) {
				measure();
				window.clearInterval(poll);
			}
		}, 250);
		const clearPoll = () => window.clearInterval(poll);
		window.setTimeout(clearPoll, 5000);

		return () => {
			mo.disconnect();
			ro?.disconnect();
			window.removeEventListener('resize', measure);
			clearPoll();
		};
	}, [enabled]);

	return right;
}

const Toaster = ({ ...props }: ToasterProps) => {
	const { theme = 'system' } = useTheme();
	const isMobile = useIsMobile();
	const desktopRight = useDesktopToastRight(!isMobile);

	const desktopStyle: React.CSSProperties | undefined = !isMobile
		? {
				top: '16px',
				right: desktopRight,
				left: 'auto',
				bottom: 'auto',
				transform: 'none',
			}
		: undefined;

	return (
		<Sonner
			theme={theme as ToasterProps['theme']}
			className="toaster group"
			position={isMobile ? 'top-center' : 'top-right'}
			offset={isMobile ? 'calc(env(safe-area-inset-top) + 11px)' : undefined}
			duration={2500}
			visibleToasts={3}
			style={desktopStyle as React.CSSProperties}
			toastOptions={{
				classNames: {
					toast: isMobile
						? 'group toast glass-obsidian glass-obsidian-flat group-[.toaster]:text-foreground group-[.toaster]:py-3 group-[.toaster]:px-4 group-[.toaster]:text-sm'
						: 'group toast glass-obsidian glass-obsidian-flat group-[.toaster]:text-foreground group-[.toaster]:py-1 group-[.toaster]:px-2 group-[.toaster]:text-xs',
					description: isMobile
						? 'group-[.toast]:text-muted-foreground group-[.toast]:text-xs'
						: 'group-[.toast]:text-muted-foreground group-[.toast]:text-[11px]',
					actionButton: isMobile
						? 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:text-xs group-[.toast]:h-8 group-[.toast]:px-3'
						: 'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:text-[10px] group-[.toast]:h-6 group-[.toast]:px-1.5',
					cancelButton: isMobile
						? 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:text-xs group-[.toast]:h-8 group-[.toast]:px-3'
						: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:text-[10px] group-[.toast]:h-6 group-[.toast]:px-1.5',
				},
			}}
			{...props}
		/>
	);
};


export { Toaster, toast };
